import { useState, useEffect, useCallback } from 'react'
import { getVoiceNotes, setVoiceNoteVerdict } from '../utils/api'
import { C, font } from '../design'

const SOURCE_LABEL = {
  journal: 'Дневник', personal: 'Личная переписка', group: 'Группа',
  threads: 'Threads', resume: 'Резюме', unknown: '?',
}
const SOURCE_COLOR = {
  journal: 'rgba(120,220,160,0.13)', personal: 'rgba(200,169,110,0.13)',
  group: 'rgba(180,185,212,0.09)', threads: 'rgba(160,120,220,0.13)',
  resume: 'rgba(120,160,255,0.13)', unknown: 'rgba(180,185,212,0.07)',
}
const FIELD_LABEL = {
  tone_note: 'Тон', reasoning_pattern: 'Манера рассуждать', formality_note: 'Формальность', emoji_note: 'Эмодзи',
}

export default function VoiceNotesViewer({ userId, onCountChange }) {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [collapsed, setCollapsed] = useState({})
  const [verdicts, setVerdicts] = useState({})
  const [pending, setPending] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getVoiceNotes(userId)
      if (res.notes) {
        setNotes(res.notes)
        onCountChange?.(res.total || 0)
        const init = {}
        for (const n of res.notes) if (n.user_verdict) init[n.id] = n.user_verdict
        setVerdicts(init)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, onCountChange])

  useEffect(() => { load() }, [load])

  const handleReject = async (noteId) => {
    const current = verdicts[noteId] || null
    const next = current === 'rejected' ? null : 'rejected'
    setPending(noteId)
    setVerdicts(p => ({ ...p, [noteId]: next }))
    try {
      await setVoiceNoteVerdict(userId, noteId, next)
    } catch {
      setVerdicts(p => ({ ...p, [noteId]: current }))
    } finally {
      setPending(null)
    }
  }

  const bySource = {}
  for (const n of notes) {
    const src = n.source_type || 'unknown'
    if (!bySource[src]) bySource[src] = []
    bySource[src].push(n)
  }
  const sourceOrder = ['journal', 'personal', 'group', 'threads', 'resume', 'unknown']
  const sources = Object.keys(bySource).sort((a, b) => sourceOrder.indexOf(a) - sourceOrder.indexOf(b))
  const rejectedCount = Object.values(verdicts).filter(v => v === 'rejected').length

  if (loading && notes.length === 0) {
    return (
      <div style={{ padding: '16px 0', color: C.textMuted, fontFamily: font.body, fontSize: 13 }}>
        загрузка наблюдений…
      </div>
    )
  }
  if (notes.length === 0) {
    return (
      <div style={{ padding: '16px 0', color: C.textMuted, fontFamily: font.body, fontSize: 13 }}>
        Наблюдений о манере говорить пока нет. Они появляются вместе с фактами при извлечении.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted }}>
          {notes.length} наблюдений
          {rejectedCount > 0 && <span style={{ color: C.red, marginLeft: 8 }}>· {rejectedCount} исключено</span>}
        </span>
      </div>

      {sources.map(src => {
        const list = bySource[src]
        const isCollapsed = collapsed[src]
        const rejectedInGroup = list.filter(n => (verdicts[n.id] ?? null) === 'rejected').length
        return (
          <div key={src} style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 10, overflow: 'hidden',
          }}>
            <button
              onClick={() => setCollapsed(p => ({ ...p, [src]: !p[src] }))}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', background: 'none', border: 'none',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{
                fontFamily: font.mono, fontSize: 10, padding: '2px 8px', borderRadius: 4,
                background: SOURCE_COLOR[src] || SOURCE_COLOR.unknown, color: C.textMuted,
              }}>
                {SOURCE_LABEL[src] || src}
              </span>
              <span style={{ fontFamily: font.heading, fontSize: 16, fontWeight: 600, color: C.text, flex: 1 }} />
              {rejectedInGroup > 0 && (
                <span style={{ fontFamily: font.mono, fontSize: 10, color: C.red }}>{rejectedInGroup} исключено</span>
              )}
              <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted }}>{list.length}</span>
              <span style={{ color: C.textMuted, fontSize: 11 }}>{isCollapsed ? '▶' : '▼'}</span>
            </button>

            {!isCollapsed && (
              <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {list.map(n => {
                  const verdict = verdicts[n.id] ?? null
                  const isRejected = verdict === 'rejected'
                  const isPending = pending === n.id
                  const fields = ['tone_note', 'reasoning_pattern', 'formality_note', 'emoji_note'].filter(k => n[k])
                  return (
                    <div key={n.id} style={{
                      display: 'flex', gap: 10, padding: '10px 12px',
                      background: isRejected ? `${C.red}12` : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isRejected ? `${C.red}30` : C.border}`,
                      borderRadius: 8, opacity: isRejected ? 0.55 : 1,
                      transition: 'all 0.15s',
                    }}>
                      <button
                        onClick={() => !isPending && handleReject(n.id)}
                        disabled={isPending}
                        title="Исключить наблюдение"
                        style={{
                          width: 22, height: 22, borderRadius: 4, flexShrink: 0,
                          border: `1px solid ${isRejected ? C.red : `${C.red}40`}`,
                          background: isRejected ? `${C.red}30` : 'none',
                          color: isRejected ? C.red : `${C.red}80`,
                          cursor: isPending ? 'wait' : 'pointer', fontSize: 11,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >✗</button>

                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                        {n.notable_phrases?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {n.notable_phrases.map((ph, i) => (
                              <span key={i} style={{
                                fontFamily: font.body, fontSize: 12, color: C.silverHi,
                                background: C.accentDim, border: `1px solid ${C.border}`,
                                borderRadius: 4, padding: '2px 7px',
                                textDecoration: isRejected ? 'line-through' : 'none',
                              }}>
                                "{ph}"
                              </span>
                            ))}
                          </div>
                        )}
                        {fields.map(k => (
                          <div key={k} style={{ fontFamily: font.body, fontSize: 12.5, color: C.textMuted }}>
                            <span style={{ fontFamily: font.mono, fontSize: 10, color: C.silverLo, marginRight: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {FIELD_LABEL[k]}
                            </span>
                            {n[k]}
                          </div>
                        ))}
                        {!n.notable_phrases?.length && fields.length === 0 && (
                          <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>
                            (пустое наблюдение)
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
